import orderBy from 'lodash/orderBy';

import {
  BEST_SELLER_FLAG,
  BEST_SELLING_FLAG,
  TRENDING_FLAG,
  defaultColorSort,
  sortAttAsc,
  sortAttDesc,
  sortAttName,
  sortAttPrice,
  sortAttRank,
} from '~/core/constants';
import { FiltersBySection, SortBy } from '~/core/enums';
import { ColorModel, Feature } from '~/core/models';
import { TypeOfFilter } from '~/core/types/sortAndFilter';

export type WithFeatures = { features: Feature[] };

const hasName =
  (comparedName: string) =>
  ({ name }: Feature): boolean =>
    name === comparedName;

export const hasTrendingOrBestSelling = (
  features: Feature[]
): string | null => {
  const matchingFeature = features.find(({ name }: Feature) =>
    new RegExp(
      `^(${TRENDING_FLAG}|${BEST_SELLING_FLAG}|${BEST_SELLER_FLAG})$`,
      'i'
    ).test(name)
  );

  if (!matchingFeature) {
    return null;
  }

  const isTrending = new RegExp(`^${TRENDING_FLAG}$`, 'i').test(
    matchingFeature.name
  );

  return isTrending
    ? 'SCROLLABLE_AREA.COLOR.ON_TREND'
    : 'SCROLLABLE_AREA.BESTSELLING';
};

export const sortFilters = (filters: TypeOfFilter[]): TypeOfFilter[] =>
  filters
    .map<TypeOfFilter>(([category, opts, position]) => [
      category,
      opts.sort((a, b) => (a.label > b.label ? 1 : -1)),
      position,
    ])
    .sort(([categoryA, _A, positionA], [categoryB, _B, positionB]) => {
      const [a, b] =
        positionA && positionB
          ? [positionA, positionB]
          : [categoryA, categoryB];

      return a > b ? 1 : -1;
    });

export const sortOptionsBy: (
  section?: FiltersBySection
) => Record<SortBy, <T extends WithFeatures>(list: T[]) => T[]> = (
  section
) => ({
  [SortBy.HighToLow]: (list) =>
    orderBy(list, [sortAttPrice, sortAttRank], [sortAttDesc, sortAttAsc]),
  [SortBy.LowToHigh]: (list) =>
    orderBy(list, [sortAttPrice, sortAttRank], [sortAttAsc, sortAttAsc]),
  [SortBy.NameAZ]: (list) => orderBy(list, [sortAttName], [sortAttAsc]),
  [SortBy.NameZA]: (list) => orderBy(list, [sortAttName], [sortAttDesc]),
  [SortBy.TrendingNow]: (list) => {
    const sortTrendDefault = () => orderBy(list, [sortAttName], [sortAttAsc]);
    const sortTrendBy: Record<FiltersBySection, () => typeof list> = {
      [FiltersBySection.COLOR]: () =>
        orderBy(
          list,
          [
            (item) => !!hasTrendingOrBestSelling(item.features),
            sortAttRank,
            sortAttName,
          ],
          [sortAttDesc, sortAttAsc, sortAttAsc]
        ),
      [FiltersBySection.APPLIANCE]: () =>
        orderBy(
          list,
          [
            (item) => !!hasTrendingOrBestSelling(item.features),
            (item) => item.features.some(hasName('promotion')),
          ],
          [sortAttDesc, sortAttDesc]
        ),
    };

    return section ? sortTrendBy[section]() : sortTrendDefault();
  },
});

export const sortAppliancesBy = sortOptionsBy(FiltersBySection.APPLIANCE);

export const sortColorsByTrending = sortOptionsBy(FiltersBySection.COLOR)[
  defaultColorSort
];

export const sortColorsBy = (sortBy: SortBy, colors: ColorModel[]) =>
  sortOptionsBy(FiltersBySection.COLOR)[sortBy](colors);
